/**
 * B060020 トータル加工用リスト
 */
import {
  msgState,
  SCodeSelect,
  SDateField,
  SFileButton,
  SFrame,
  SLabel,
  SNumberField,
  SText,
  STextField,
} from "components";
import { B060020HakkoKbn, getSCode, SCode, SeisanKbn } from "messages/codes";
import { labels } from "messages/labels";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { date, download, printed } from "services";
import { focusState, useFocus } from "states";
import {
  B060020Print,
  B000111KakoshubetsuNmFrom,
  B000111KakoshubetsuNmTo,
  B000200ShohinNmFrom,
  B000200ShohinNmTo,
  B000200JanNmFrom,
  B000200JanNmTo,
  B060020Request,
  catchFileError,
} from "clients/rest";
import { meishoKbn } from "services/codes";
import { B000111 } from "pages";
import { B000200 } from "pages/b000/B000200";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { getClientMessage } from "messages";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  hakoKbn: SCode;
  seisakuYmdFrom: string;
  seisakuYmdTo: string;
  seizoNoFrom: string;
  seizoNoTo: string;
  juchudenpyoNoFrom: string;
  juchudenpyoNoTo: string;
  kakoshubetsuCdFrom: string;
  kakoshubetsuNmFrom: string;
  kakoshubetsuCdTo: string;
  kakoshubetsuNmTo: string;
  shohinCdFrom: string;
  shohinNmFrom: string;
  shohinCdTo: string;
  shohinNmTo: string;
  janCdFrom: string;
  janNmFrom: string;
  janCdTo: string;
  janNmTo: string;
  seisanKbn: SCode;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  hakoKbn: getSCode(B060020HakkoKbn, "0"),
  seisakuYmdFrom: date(),
  seisakuYmdTo: date(),
  seizoNoFrom: "",
  seizoNoTo: "",
  juchudenpyoNoFrom: "",
  juchudenpyoNoTo: "",
  kakoshubetsuCdFrom: "",
  kakoshubetsuNmFrom: "",
  kakoshubetsuCdTo: "",
  kakoshubetsuNmTo: "",
  shohinCdFrom: "",
  shohinNmFrom: "",
  shohinCdTo: "",
  shohinNmTo: "",
  janCdFrom: "",
  janNmFrom: "",
  janCdTo: "",
  janNmTo: "",
  seisanKbn: undefined,
};

const focusId = "B060020";

export const B060020 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: Props) => {
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  // フォーカス関数
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);

  // 画面遷移
  const navigate = useNavigate();

  //初期表示
  useEffect(() => {
    focus(1);
  });

  //印刷ボタンクリックイベント
  const handleClickPrintButton = () => {
    // 印刷フォームのデータを取得する。
    const fields = methods.getValues();
    const param: B060020Request = {
      kakolisthakozumiKbn: fields.hakoKbn?.value!,
      seisakuYmdFrom: fields.seisakuYmdFrom,
      seisakuYmdTo: fields.seisakuYmdTo,
      seizoNoFrom: fields.seizoNoFrom,
      seizoNoTo: fields.seizoNoTo,
      juchudenpyoNoFrom: fields.juchudenpyoNoFrom,
      juchudenpyoNoTo: fields.juchudenpyoNoTo,
      kakoshubetsuCdFrom: fields.kakoshubetsuCdFrom,
      kakoshubetsuCdTo: fields.kakoshubetsuCdTo,
      shohinCdFrom: fields.shohinCdFrom,
      shohinCdTo: fields.shohinCdTo,
      janCdFrom: fields.janCdFrom,
      janCdTo: fields.janCdTo,
      shohinshubetsuKbn: fields.seisanKbn?.value,
    };
    B060020Print(param)
      .then((response) => {
        if (printed(response)) {
          setMsg(getClientMessage("B060020_001"));
        } else {
          download(response);
          setMsg(getClientMessage("B060020_002"));
        }
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="トータル加工用リスト"
        gridTemplateColumns="6rem 5rem 5rem 1rem 9rem 1rem 4rem 4rem 1rem 1rem 5rem 5rem 20rem 4rem"
      >
        <SLabel>発行区分</SLabel>
        <SCodeSelect
          name="hakoKbn"
          options={B060020HakkoKbn}
          {...register(1)}
          gridColumn="span 2"
        />
        <Box />
        <SLabel gridColumn="1">製作日</SLabel>
        <SDateField
          name="seisakuYmdFrom"
          {...register(2)}
          gridColumn="span 2"
        />
        <SText>～</SText>
        <SDateField name="seisakuYmdTo" {...register(3)} />
        <SLabel gridColumn="1">製造番号</SLabel>
        <SNumberField
          name="seizoNoFrom"
          code
          size={4}
          gridColumn="span 2"
          {...register(4)}
        />
        <SText>～</SText>
        <SNumberField name="seizoNoTo" code size={4} {...register(5)} />
        <SLabel gridColumn="1">受注No.</SLabel>
        <SNumberField
          name="juchudenpyoNoFrom"
          code
          size={10}
          {...register(6)}
          gridColumn="span 2"
        />
        <SText>～</SText>
        <SNumberField name="juchudenpyoNoTo" code size={10} {...register(7)} />
        <Box gridColumn={6} />
        <SLabel gridColumn="1">加工種別</SLabel>
        <SNumberField
          name="kakoshubetsuCdFrom"
          code
          size={3}
          {...register(8)}
          onBlur={() => B000111KakoshubetsuNmFrom(methods, navigate, setMsg)}
          dialog={<B000111 meishoKbn={meishoKbn.kakoshubetsu} />}
          handleSubmit={(data: any) => {
            methods.setValue("kakoshubetsuCdFrom", data.meishoCd);
            methods.setValue("kakoshubetsuNmFrom", data.meishoNm1);
          }}
        />
        <STextField
          name="kakoshubetsuNmFrom"
          size={40}
          disabled
          gridColumn="span 7"
        />
        <SText>～</SText>
        <SNumberField
          name="kakoshubetsuCdTo"
          code
          size={3}
          {...register(9)}
          onBlur={() => B000111KakoshubetsuNmTo(methods, navigate, setMsg)}
          dialog={<B000111 meishoKbn={meishoKbn.kakoshubetsu} />}
          handleSubmit={(data: any) => {
            methods.setValue("kakoshubetsuCdTo", data.meishoCd);
            methods.setValue("kakoshubetsuNmTo", data.meishoNm1);
          }}
        />
        <STextField
          name="kakoshubetsuNmTo"
          size={40}
          disabled
          gridColumn="span 2"
        />
        <SLabel gridColumn="1">商品</SLabel>
        <SNumberField
          name="shohinCdFrom"
          code
          size={10}
          {...register(10)}
          onBlur={() => B000200ShohinNmFrom(methods, navigate, setMsg)}
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("shohinCdFrom", data.shohinCd);
            methods.setValue("shohinNmFrom", data.shohinNm);
          }}
          gridColumn="span 2"
        />
        <STextField
          name="shohinNmFrom"
          size={24}
          disabled
          gridColumn="span 5"
        />
        <SText>～</SText>
        <SNumberField
          name="shohinCdTo"
          code
          size={10}
          {...register(11)}
          onBlur={() => B000200ShohinNmTo(methods, navigate, setMsg)}
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("shohinCdTo", data.shohinCd);
            methods.setValue("shohinNmTo", data.shohinNm);
          }}
          gridColumn="span 3"
        />
        <STextField name="shohinNmTo" size={24} disabled />
        <SLabel gridColumn="1">JANコード</SLabel>
        <SNumberField
          name="janCdFrom"
          code
          size={13}
          {...register(12)}
          onBlur={() => B000200JanNmFrom(methods, navigate, setMsg)}
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("janCdFrom", data.janCd);
            methods.setValue("janNmFrom", data.shohinNm);
          }}
          gridColumn="span 2"
        />
        <STextField name="janNmFrom" size={24} disabled gridColumn="span 5" />
        <SText>～</SText>
        <SNumberField
          name="janCdTo"
          onBlur={() => B000200JanNmTo(methods, navigate, setMsg)}
          code
          size={13}
          {...register(13)}
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("janCdTo", data.janCd);
            methods.setValue("janNmTo", data.shohinNm);
          }}
          gridColumn="span 3"
        />
        <STextField name="janNmTo" size={24} disabled />
        <SLabel gridColumn="1">生産区分</SLabel>
        <SCodeSelect
          name="seisanKbn"
          options={SeisanKbn}
          empty
          {...register(14)}
          gridColumn="span 2"
        />
        <SFileButton
          label={labels.buttons.print}
          gridRow={9}
          gridColumn={14}
          {...register(15)}
          onClick={handleClickPrintButton}
          align="right"
        />
        <Box />
      </SFrame>
    </FormProvider>
  );
};
